import type { HighlightCard } from '@/lib/api-client';
import { VisualThumb, hasThumb } from '@/components/core/visual';
import { cn } from '@/lib/utils';
import { SlideText } from './_shared/slideText';
import {
  resolveSlideRole,
  type HighlightTemplate,
  type TemplateVariant,
} from './types';

interface TemplateCardProps {
  template: HighlightTemplate;
  card: HighlightCard;
  slideIndex: number;
  totalSlides: number;
  conceptName?: string;
  variant?: TemplateVariant;
  className?: string;
}

export function TemplateCard({
  template,
  card,
  slideIndex,
  totalSlides,
  conceptName,
  variant = 'full',
  className,
}: TemplateCardProps) {
  // A thumbnail of a card with a picture is the picture itself (Quick Bytes,
  // concept grids): a poster you recognise the concept by. A drawn diagram
  // keeps the designed poster - shrunk, it would be unreadable.
  if (variant === 'thumbnail' && card.visual && hasThumb(card.visual)) {
    return (
      <div
        className={cn(
          'flex h-full w-full flex-col items-center justify-center gap-2 bg-white p-3',
          className
        )}
        data-visual-poster
      >
        <VisualThumb
          visual={card.visual}
          className='max-h-[78%] w-auto max-w-full border-0'
        />
        <p className='line-clamp-1 text-center text-xs font-semibold text-slate-900'>
          <SlideText text={card.title} />
        </p>
      </div>
    );
  }

  const Layout =
    variant === 'thumbnail'
      ? (template.thumbnail ?? template.layouts.cover)
      : template.layouts[resolveSlideRole(slideIndex, totalSlides)];

  return (
    <div
      className={cn(
        'h-full w-full motion-safe:animate-fade-in',
        template.containerClassName,
        className
      )}
    >
      <Layout
        card={card}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        conceptName={conceptName}
      />
    </div>
  );
}
